import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Observable, OperatorFunction } from 'rxjs';
import { catchError } from 'rxjs/operators';

const TIMEOUT_ERROR_MESSAGE = 'timeout';
const UNKNOWN_ERROR_STATUS = 0;
const TIMEOUT_ERROR_STATUS = new Set([
  UNKNOWN_ERROR_STATUS,
  HttpStatusCode.GatewayTimeout,
  HttpStatusCode.RequestTimeout,
  HttpStatusCode.ServiceUnavailable,
]);

/**
 * Custom RxJS operator that catches timeout errors from the API and executes a fallback function.
 * The function should return a new observable to continue the stream.
 *
 * @param selector A function that receives the timeout error and returns a new Observable
 * @returns An operator function that can be used in a pipe
 *
 * @example
 * ```typescript
 * this.http.get('/api/data').pipe(
 *   catchTimeoutError((error) => {
 *     console.log('Timeout occurred, using fallback');
 *     return of(defaultData);
 *   })
 * )
 * ```
 */
export function catchTimeoutError<T>(
  selector: (error: HttpErrorResponse) => Observable<T>
): OperatorFunction<T, T> {
  return (source: Observable<T>) =>
    source.pipe(
      catchError((error: HttpErrorResponse) => {
        if (isTimeoutError(error)) {
          return selector(error);
        }
        throw error;
      })
    );
}

/**
 * Checks if the error is a timeout error from the API
 * @param httpError The HTTP error response
 * @returns true if the error is a timeout error
 */
function isTimeoutError(httpError: HttpErrorResponse): boolean {
  // Check if error message contains timeout indicators
  const message = errorMessage(httpError);
  const hasTimeoutMessage = message
    .toLowerCase()
    .includes(TIMEOUT_ERROR_MESSAGE);

  // Check if error status is regarded as a timeout status
  const hasTimeoutStatus = TIMEOUT_ERROR_STATUS.has(httpError.status);

  return hasTimeoutMessage || hasTimeoutStatus;
}

/**
 * Extracts the error message from the HTTP error response
 * @param httpError The HTTP error response
 * @returns The error message
 */
function errorMessage(httpError: HttpErrorResponse): string {
  if (typeof httpError.error === 'string') {
    return httpError.error;
  }

  return (
    httpError.error?.message || httpError.error?.msg || httpError.message || ''
  );
}
